import { isDesignTime } from '@platform/runtime'

const rEmulator = /^(.*\/)?(emulator|design-components)\.(css|js)(\?.*)?$/i
const rComponent = /^(.*\/)?(system_|bizcode_)[\w-]+(\.(css|js))?(\?.*)?$/i

const rCodeVendor = /^(\/)?@?([a-zA-Z](?:.*?)@\d+\.\d+\.\d+(?:.*?))/
const rNamespace = /\/custom\/component\/(.*?)\//

const systemVendorPrefix = '@system-vendor'
const customVendorPrefix = '@custom-vendor'

const rSystemVendor = /\/@system-vendor\//
const rCustomVendor = /\/@custom-vendor\//
const rCNamespace =  /@custom-vendor\/(.*?)(@\d+\.\d+\.\d+(?:.*?))((\/)|\.js)/
const rCVendorId = /@custom-vendor\/(.*?)\.js/

const systemComponentRoot = '/studio/system/component/'
const customComponentRoot = '/studio/custom/component/'
const systemVendorRoot = '/studio/system/'
const customVendorRoot = '/studio/custom/'

const COMPONENT_COMMON_PREFIX = '@app-component-common/'
const COMPONENT_COMMON_PREFIX_RE = /\/?(app-component-common@.*?)\//
const INTERNAL_VENDOR_VENDOR_RE = /\/?((@mypaas\/honeycomb-mobile-ui)@[^\/\s]*)/

const PURE_UI_PREFIX = '@_@pureui/vue/'
const PURE_UI_PREFIX_RE = /\/?@pureui\/vue@.+/

const LODASH_RE = /^\/?lodash(@\d+\.\d+\.\d+)?(?:\/(.+)|$)$/i
const LODASH_PREFIX = '@lodash'
const LODASH_PREFIX_RE = new RegExp(LODASH_PREFIX, 'i')

const pathsReplacer = [
    [/\/@system-component\//, systemComponentRoot],
    [/\/@custom-component\//, customComponentRoot],
]
const namespaceData = {}

// 加载样式文件（避免重复加载）
function loadCss(url) {
    if (process.env.VUE_APP_TARGET) {
        url = process.env.VUE_APP_TARGET + url
    }

    if (document.head.querySelector(`link[href="${url}"][rel=stylesheet]`)) return
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = url
    document.head.appendChild(link)
}

const lodashPath = `${systemVendorRoot}internal-vendor/lodash/latest/index.js`

function withTimestamp(id, timestamp, check) {
    return check && timestamp ? `${id}?v=${timestamp}` : id
}

function formatEmulator(id, timestamp) {
    return withTimestamp(id, timestamp, rEmulator.test(id))
}

function formatComponent(id, timestamp) {
    return withTimestamp(id, timestamp, rComponent.test(id))
}

function getFormattedVendorUri(id, timestamp) {
    if (id) {
        const idArray = id.split('/')
        const uri = /^@/.test(idArray[0]) ? idArray.slice(0, 2).join('/') : idArray[0]
        const uriArray = uri.split('@')
        if (uriArray.length > 1) {
            const file = uriArray.join('/')
                .replace(/^\//, '@')
                .replace(`?v=${timestamp}`, '')
                .replace(/\.js$/, '')
            return `${file}/index.js?v=${timestamp}`
        }
    }
    return undefined
}

function formatVendor(uri, timestamp, components) {
    if (new RegExp('/@mypaas/honeycomb-mobile-ui/').test(uri)) {
        loadCss(`${systemVendorRoot}internal-vendor/@mypaas/honeycomb-mobile-ui/latest/index.css?v=${timestamp}`)
        return `${systemVendorRoot}internal-vendor/@mypaas/honeycomb-mobile-ui/latest/index.js?v=${timestamp}`
    }
    if (new RegExp(COMPONENT_COMMON_PREFIX).test(uri)) {
        return `${systemVendorRoot}internal-vendor/app-component-common/latest/index.js?v=${timestamp}`
    }

    if (new RegExp(`${PURE_UI_PREFIX.slice(0, -1)}/?`).test(uri)) {
        return `${systemVendorRoot}internal-vendor/@pureui/vue/latest/index.js?v=${timestamp}`
    }

    if (LODASH_PREFIX_RE.test(uri)) {
        return `${lodashPath}?v=${timestamp}`
    }

    if (rSystemVendor.test(uri)) {
        const uriArray = uri.split(rSystemVendor)
        const from = getFormattedVendorUri(uriArray.slice(1).join(systemVendorPrefix), timestamp)
        if (from) {
            return `${systemVendorRoot}common-vendor/${from}`
        }
    } else if (rCustomVendor.test(uri)) {
        const uriArray = uri.split(rCustomVendor)
        const id = uriArray.slice(1).join(customVendorPrefix)
        const idArray = id.split('/')
        const from = getFormattedVendorUri(idArray.slice(1).join('/'), timestamp)
        if (from) {
            return `${customVendorRoot}common-vendor/${idArray[0]}/${from}`
        }
    } else if (uri.includes(systemComponentRoot)) {
        const vIndex = uri.indexOf('?')
        if (vIndex !== -1) {
            uri = uri.substr(0, vIndex)
            const comp = components.find(compont => uri.includes(compont.packageName))
            // 如果加载组件在系统组件里匹配上，添加最后更新时间为版本号
            if (comp && comp.updatedAt) {
                return `${uri}?v=${(new Date(comp.updatedAt)).getTime()}`
            }
        }

        return uri
    }
    return undefined
}

const getState = () => (isDesignTime ? require('@store').default?.state?.modeling?.app : require('@/preview/store').default?.state?.preview?.app)

function getFormattedUri(uri, timestamp, components) {
    const vendorUri = formatVendor(uri, timestamp, components)

    return vendorUri || uri
}

function formatModules(id, timestamp, pool, isRuntime) {
    for (let i = 0; i < pathsReplacer.length; i++) {
        const [regexp, target] = pathsReplacer[i]
        if (regexp.test(id)) {
            const file = `${id.replace(regexp, target)}`
            return `${isRuntime ? file : file.replace('.js', '.design.js')}?v=${timestamp}`
        }
    }
    let namespace = ''
    if (id) {
        const matched = id.match(rCNamespace)
        namespace = matched && matched[1] ? `${matched[1]}`.replace(`${pool.currentNamespace}/`, '').concat(matched[2]) : ''
    }
    if (rCustomVendor.test(id) || rSystemVendor.test(id)) {
        id += `?v=${timestamp}`
    }
    if (rCustomVendor.test(id) && rCodeVendor.test(namespace) && rCVendorId.test(id)) {
        const regexp = `/${customVendorPrefix}`
        const dataId = id.match(rCVendorId)[1]
        const { isTenant, fromTeamCode, teamCode } = getState()
        const currentNamespace = window.__resolve_vendor_integrity || (isTenant ? fromTeamCode : teamCode)
        const target = `/${customVendorPrefix}/${currentNamespace}`
        if (pool[dataId] && !pool[dataId].length) {
            delete pool[dataId]
        }
        if (id.includes(pool.currentNamespace)) {
            return id
        }
        return id.replace(regexp, target)
    }
    return id
}

function formatVendorId(vendorId, namespace) {
    return vendorId.replace(rCodeVendor, ($0, $1) => {
        if ($1) {
            return `${systemVendorPrefix}${$0}`
        }
        return `${customVendorPrefix}/${namespace}${$0}`
    })
}

function formatLodashId(id) {
    const match = id.match(LODASH_RE)
    if (match[2]) return `${LODASH_PREFIX}/${match[2]}`
    return LODASH_PREFIX
}

function setNamespace(id, namespace) {
    namespaceData.currentNamespace = namespace
    if (namespaceData[id]) {
        namespaceData[id].push(namespace)
    } else {
        namespaceData[id] = [namespace]
    }
}

function resolveModule(data) {
    const { id } = data

    if (id && INTERNAL_VENDOR_VENDOR_RE.test(id)) {
        data.id = data.id.replace(INTERNAL_VENDOR_VENDOR_RE, '@mypaas/honeycomb-mobile-ui')
        if (/honeycomb-mobile-ui$/.test(data.id)) {
            data.id += '/index.js'
        }
        return
    }
    if (id && COMPONENT_COMMON_PREFIX_RE.test(id)) {
        data.id = data.id.replace(COMPONENT_COMMON_PREFIX_RE, COMPONENT_COMMON_PREFIX)
    } else if (id && PURE_UI_PREFIX_RE.test(id)) {
        // 区分 @pureui/vue@1.0.1 or @pureui/vue@1.0.1/lib 两种方式
        if (/\/?@pureui\/vue@.*?\//.test(id)) {
            data.id = data.id.replace(/\/?@pureui\/vue@.*?\//, PURE_UI_PREFIX)
        } else {
            data.id = PURE_UI_PREFIX.slice(0, -1)
        }
    } else if (id && LODASH_RE.test(id)) {
        data.id = formatLodashId(data.id)
    } else if (id && rCodeVendor.test(id)) {
        let namespace = ''
        let matched = []
        if (data.refUri) {
            matched = data.refUri.match(rNamespace)
        }
        if (matched && matched[1]) {
            namespace = `${matched[1]}/`
            setNamespace(data.id, matched[1])
        }
        data.id = formatVendorId(data.id, namespace)
    }
}

export {
    formatEmulator,
    formatComponent,
    getFormattedUri,
    formatModules,
    formatVendorId,
    resolveModule,
    namespaceData,
}
